<section class="page">

  <!-- HERO -->
  <header class="hero">
    <div class="hero__bg">
      <div class="hero__orb hero__orb--primary"></div>
      <div class="hero__orb hero__orb--secondary"></div>
    </div>
    <div class="hero__content">
      <div class="hero__kicker">Comparison</div>
      <h1 class="hero__title">Partiri vs Fly.io</h1>
      <p class="hero__subtitle">
        Partiri is a European-first PaaS: every live region sits inside the EU today, pricing is a
        fixed monthly rate per pod with no subscription tiers, and deployment works from Git, a
        container registry, the CLI, or an MCP server for AI agents. Fly.io takes a different,
        Docker-first approach, running your app as a Firecracker microVM in whichever of its many
        regions you choose. Here is how the two compare, row by row.
      </p>
    </div>
  </header>

  <!-- COMPARISON TABLE -->
  <section class="section">
    <div class="section__head">
      <h2 class="section__title">Partiri vs Fly.io, feature by feature</h2>
    </div>

    <div class="compare-table-wrap">
      <table class="compare-table">
        <caption>Comparison of Partiri and Fly.io across data residency, pricing, runtimes, storage, deployment workflow, and free tier</caption>
        <thead>
          <tr>
            <th scope="col">Category</th>
            <th scope="col">Partiri</th>
            <th scope="col">Fly.io</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row" data-label="Category">Data residency &amp; regions</th>
            <td class="compare-table__partiri" data-label="Partiri">
              Live in Germany (Nuremberg) and Finland (Helsinki) today; every region on the public
              roadmap is a European country, with 46 targeted in total.
            </td>
            <td data-label="Fly.io">
              A large number of regions worldwide, including several in Europe (for example Amsterdam
              and Frankfurt). Check fly.io/docs/reference/regions for the current list.
            </td>
          </tr>
          <tr>
            <th scope="row" data-label="Category">Pricing model</th>
            <td class="compare-table__partiri" data-label="Partiri">
              Pick a pod size (vCPU + RAM + storage bundle) and pay a fixed monthly rate for it &mdash;
              no subscription tiers or platform fee. New accounts start with signup credit.
            </td>
            <td data-label="Fly.io">
              Usage-based billing for compute (charged while a Machine is running) with volumes and
              bandwidth billed separately. Check fly.io/docs/about/pricing for current numbers.
            </td>
          </tr>
          <tr>
            <th scope="row" data-label="Category">Supported runtimes</th>
            <td class="compare-table__partiri" data-label="Partiri">
              Native support for Node.js, Deno, Python, Go, Rust, Ruby, Elixir, PHP, JVM (Java/Kotlin),
              .NET, and C++, plus static sites or any Docker image.
            </td>
            <td data-label="Fly.io">
              Any language, but always packaged as a Docker image &mdash; the <code>fly launch</code>
              command can generate a Dockerfile for common frameworks, or you can supply your own.
            </td>
          </tr>
          <tr>
            <th scope="row" data-label="Category">Persistent storage</th>
            <td class="compare-table__partiri" data-label="Partiri">
              Persistent disks (1&ndash;10 GB) attachable to web services and private services, provisioned
              in the same EU region as the workload that uses them.
            </td>
            <td data-label="Fly.io">
              Fly Volumes attach block storage to a Machine in a single region. Fly also offers a
              managed Postgres option &mdash; check fly.io/docs for current availability and scope.
            </td>
          </tr>
          <tr>
            <th scope="row" data-label="Category">Deployment workflow</th>
            <td class="compare-table__partiri" data-label="Partiri">
              Git push (GitHub, GitLab, Bitbucket, Codeberg), a container registry (GHCR, GitLab
              Container Registry, Docker Hub, Artifact Registry, ECR), the <code>partiri</code> CLI, or
              a hosted MCP server for AI coding agents.
            </td>
            <td data-label="Fly.io">
              Primarily the <code>flyctl</code> CLI (<code>fly deploy</code>) building from a
              Dockerfile, or deploying a pre-built image from a registry. Git-push-to-deploy is
              typically wired up via a GitHub Actions integration rather than built in natively.
            </td>
          </tr>
          <tr>
            <th scope="row" data-label="Category">Free tier / trial credit</th>
            <td class="compare-table__partiri" data-label="Partiri">
              Every new account starts with &euro;4 in credit &mdash; enough to run a Nano pod free for
              the first month.
            </td>
            <td data-label="Fly.io">
              Usage-based billing from the first deployment; free allowances and promotional credit have
              varied over time. Check fly.io for current terms.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p class="footnote">
      Fly.io details reflect publicly available documentation at the time of writing. Pricing and
      feature availability change &mdash; confirm current specifics on fly.io before switching.
    </p>
  </section>


  <!-- CTA -->
  <section class="section">
    <div class="cta-band">
      <h2 class="cta-band__title">Ready to deploy on EU infrastructure?</h2>
      <p class="cta-band__desc">
        There is no dedicated Fly.io migration guide yet, but getting started is the same either way:
        connect a Git repository or container image, pick a pod size and region, and deploy.
      </p>
      <div class="cta-band__actions">
        <a mat-flat-button routerLink="/documentation/start" class="btn-primary">
          <mat-icon>rocket_launch</mat-icon>
          Deploy your first app
        </a>
        <a mat-outlined-button routerLink="/documentation/deployments" class="btn-ghost">
          <mat-icon>description</mat-icon>
          Deployment docs
        </a>
      </div>
    </div>
  </section>

</section>
